import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, CheckCircle2 } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { SectionHeading } from '../components/SectionHeading';
import { CTASection } from '../components/CTASection';
import { PageHero } from '../components/PageHero';
export function Werkwijze() {
  const steps = [{
    num: '01',
    title: 'Vrijblijvende Intake',
    desc: 'We starten met een vertrouwelijk gesprek om uw situatie, doelstellingen en de complexiteit van de onderneming te begrijpen. Op basis hiervan ontvangt u een helder voorstel.'
  }, {
    num: '02',
    title: 'Data Verzameling',
    desc: 'Na akkoord vragen we de benodigde informatie op via een beveiligde omgeving. Denk aan historische jaarrekeningen, prognoses en informatie over de markt en organisatie.'
  }, {
    num: '03',
    title: 'Analyse & Normalisatie',
    desc: "We analyseren de cijfers en 'normaliseren' deze. Dit betekent dat we incidentele baten/lasten of niet-marktconforme management fees corrigeren om de werkelijke verdiencapaciteit te bepalen."
  }, {
    num: '04',
    title: 'Waarderingsmodel',
    desc: 'We bouwen een op maat gemaakt financieel model, meestal gebaseerd op de Discounted Cash Flow (DCF) methode, aangevuld met marktmultiples ter controle.'
  }, {
    num: '05',
    title: 'Concept & Bespreking',
    desc: 'U ontvangt een conceptrapport. We bespreken de uitkomsten, de gehanteerde aannames en de belangrijkste waardedrivers uitgebreid met u.'
  }, {
    num: '06',
    title: 'Definitief Rapport',
    desc: 'Na de bespreking verwerken we eventuele aanvullingen en ontvangt u het definitieve, ondertekende waarderingsrapport dat u kunt gebruiken voor uw doel.'
  }];
  return <div className="w-full" data-id="element-663">
      <PageHero title={<>
            Onze <span className="font-medium" data-id="element-665">Werkwijze</span>
          </>} subtitle="Een transparant, gestructureerd proces dat leidt tot een robuust en objectief waarderingsrapport. Zonder verrassingen." data-id="element-664" />

      <section className="py-16 border-b border-warmgray/50 bg-[#F8F6F3]" data-id="element-666">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-667">
          <AnimatedSection className="max-w-3xl mx-auto text-center" data-id="element-668">
            <h2 className="text-3xl font-light text-charcoal mb-6" data-id="element-669">
              Waarom een gestructureerde aanpak?
            </h2>
            <div className="prose prose-lg text-charcoal/70 mx-auto" data-id="element-670">
              <p className="mb-4 text-left" data-id="element-671">
                Een betrouwbare waardering vereist meer dan het invullen van een
                formule. Het vraagt om een diepgaand begrip van uw
                bedrijfsmodel, de markt waarin u opereert en de specifieke
                risico's. Onze gestructureerde aanpak garandeert dat geen enkel
                detail over het hoofd wordt gezien.
              </p>
              <p className="text-left" data-id="element-672">
                Door dit vaste stappenplan te volgen, leveren we consistente
                kwaliteit en een rapport dat standhoudt in elke situatie.{' '}
                <Link to="/bedrijfswaardering" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-673">
                  Bekijk onze bedrijfswaardering dienst
                </Link>{' '}
                voor meer informatie over de voordelen.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-24 bg-offwhite pt-[20px] pb-[20px] mt-[0px] mb-[40px]" data-id="element-674">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-675">
          <div className="max-w-4xl mx-auto" data-id="element-676">
            <div className="space-y-16 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-warmgray before:to-transparent" data-id="element-677">
              {steps.map((step, index) => <AnimatedSection key={index} delay={index * 0.1} className="relative flex items-start justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-678">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-charcoal text-white text-sm font-semibold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-679">
                    {step.num}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] text-left" data-id="element-680">
                    <h3 className="text-lg font-medium text-charcoal mb-2" data-id="element-681">
                      {step.title}
                    </h3>
                    <p className="text-charcoal/60 text-sm leading-relaxed" data-id="element-682">
                      {step.desc}
                    </p>
                  </div>
                </AnimatedSection>)}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" data-id="element-683">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-684">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-685">
            <AnimatedSection className="bg-offwhite p-10 rounded-3xl" data-id="element-686">
              <div className="flex items-center gap-4 mb-6" data-id="element-687">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-accent shadow-sm" data-id="element-688">
                  <Clock size={24} data-id="element-689" />
                </div>
                <h3 className="text-2xl font-light" data-id="element-690">Doorlooptijd</h3>
              </div>
              <p className="text-charcoal/70 mb-6" data-id="element-691">
                Een standaard waarderingstraject neemt gemiddeld{' '}
                <strong data-id="element-692">3 tot 5 weken</strong> in beslag, gerekend vanaf het
                moment dat alle benodigde informatie is aangeleverd.
              </p>
              <p className="text-charcoal/70 mb-6" data-id="element-693">
                Heeft u spoed? In overleg is een versneld traject (fast-track)
                vaak mogelijk, afhankelijk van de beschikbaarheid van data en de
                complexiteit van de onderneming.
              </p>
              <Link to="/contact" className="text-accent font-medium hover:underline" data-id="element-694">
                Bespreek uw planning
              </Link>
            </AnimatedSection>

            <AnimatedSection delay={0.2} className="bg-charcoal text-white p-10 rounded-3xl" data-id="element-695">
              <h3 className="text-2xl font-light mb-8" data-id="element-696">Wat u ontvangt</h3>
              <ul className="space-y-6" data-id="element-697">
                {['Een uitgebreid, leesbaar rapport in PDF-formaat.', 'Onderbouwing van de gehanteerde methodiek en aannames.', "Inzicht in de belangrijkste waardedrivers en risico's.", 'Een presentatie/bespreking van de uitkomsten.'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-698">
                    <CheckCircle2 className="text-accent shrink-0 mt-1" size={20} data-id="element-699" />
                    <span className="text-warmgray/80" data-id="element-700">{item}</span>
                  </li>)}
              </ul>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-16 bg-offwhite border-t border-warmgray/50" data-id="element-701">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 text-center" data-id="element-702">
          <AnimatedSection className="max-w-2xl mx-auto" data-id="element-703">
            <h2 className="text-2xl font-light text-charcoal mb-4" data-id="element-704">
              Lees meer over waarderingsmethoden
            </h2>
            <p className="text-charcoal/70 mb-8" data-id="element-705">
              Wilt u meer weten over de technische kant van ons werk? Ontdek hoe
              methoden zoals de DCF en EBITDA multiples in de praktijk worden
              toegepast.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-706">
              <Link to="/kennisbank/methodes/ebitda-multiple-uitleg" className="inline-block bg-white border border-warmgray/50 hover:border-accent text-charcoal px-6 py-3 rounded-full text-sm font-medium transition-all shadow-sm" data-id="element-707">
                Uitleg EBITDA Multiple
              </Link>
              <Link to="/bedrijfswaardering" className="inline-block bg-accent hover:bg-accent-hover text-white px-6 py-3 rounded-full text-sm font-medium transition-all shadow-subtle" data-id="element-708">
                Start waardebepaling
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <CTASection data-id="element-709" />
    </div>;
}